import React, { useMemo } from 'react';

/**
 * FreeThrowVideoOverlay — Augmented reality overlay for FT video playback
 *
 * Renders phase-synced biomechanical markers over the video:
 *   - Phase badge (current biomechanical phase)
 *   - Knee angle arc visualization
 *   - Heel elevation indicator (toe duration tracking)
 *   - Ball position marker at initial raise
 *   - Speed timer between active markers
 *   - Follow-through hold marker
 *
 * Synced to currentTime from the parent video player.
 */

const PHASE_COLORS = {
  stance: '#6b7280',
  dip: '#f59e0b',
  raise: '#6366f1',
  set_point: '#8b5cf6',
  release: '#22c55e',
  follow_through: '#06b6d4',
  recovery: '#6b7280',
  no_shot: '#374151',
};

const PHASE_LABELS = {
  stance: 'STANCE',
  dip: 'DIP',
  raise: 'RAISE',
  set_point: 'SET POINT',
  release: 'RELEASE',
  follow_through: 'FOLLOW-THROUGH',
  recovery: 'RECOVERY',
  no_shot: '—',
};

const OUTCOME_COLORS = {
  made: '#22c55e',
  missed_short: '#ef4444',
  missed_long: '#f97316',
  missed_left: '#8b5cf6',
  missed_right: '#06b6d4',
  missed_rim_in_out: '#eab308',
  missed_airball: '#ec4899',
  not_visible: '#6b7280',
};

function hudBadge(color) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '3px 10px',
    borderRadius: 6,
    fontSize: 10,
    fontWeight: 700,
    fontFamily: 'monospace',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    background: 'rgba(0,0,0,0.6)',
    backdropFilter: 'blur(8px)',
    border: `1px solid ${color}66`,
    color: '#fff',
  };
}

function hudDot(color) {
  return {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: color,
    boxShadow: `0 0 8px ${color}88`,
  };
}

/**
 * Determine current phase from analysis timestamps + currentTime
 */
function getCurrentPhase(speed, clipStart, currentTime) {
  if (!speed) return 'no_shot';
  const elapsed = (currentTime - (clipStart || 0)) * 1000; // ms

  if (speed.heel_land_ms > 0 && elapsed >= speed.heel_land_ms) return 'recovery';
  if (speed.follow_through_hold_ms > 0 && elapsed >= speed.follow_through_hold_ms) return 'follow_through';
  if (speed.release_ms > 0 && elapsed >= speed.release_ms) return 'release';
  if (speed.set_point_ms > 0 && elapsed >= speed.set_point_ms) return 'set_point';
  if (speed.raise_start_ms > 0 && elapsed >= speed.raise_start_ms) return 'raise';
  if (speed.dip_start_ms > 0 && elapsed >= speed.dip_start_ms) return 'dip';
  if (speed.stance_start_ms >= 0) return 'stance';
  return 'no_shot';
}

/**
 * Compute running timer between current marker pair
 */
function getPhaseTimer(speed, clipStart, currentTime) {
  if (!speed) return null;
  const elapsed = (currentTime - (clipStart || 0)) * 1000;

  const markers = [
    { key: 'stance', ms: speed.stance_start_ms || 0 },
    { key: 'dip', ms: speed.dip_start_ms || 0 },
    { key: 'raise', ms: speed.raise_start_ms || 0 },
    { key: 'set_point', ms: speed.set_point_ms || 0 },
    { key: 'release', ms: speed.release_ms || 0 },
    { key: 'follow_through', ms: speed.follow_through_hold_ms || 0 },
    { key: 'heel_land', ms: speed.heel_land_ms || 0 },
  ].filter(m => m.ms > 0).sort((a, b) => a.ms - b.ms);

  for (let i = 0; i < markers.length - 1; i++) {
    if (elapsed >= markers[i].ms && elapsed < markers[i + 1].ms) {
      const diff = elapsed - markers[i].ms;
      return {
        from: markers[i].key,
        to: markers[i + 1].key,
        elapsed_ms: diff,
        label: `${(diff / 100).toFixed(1)}t`,
      };
    }
  }
  return null;
}

export default function FreeThrowVideoOverlay({
  attempt,
  currentTime = 0,
  clipStart = 0,
  visible = true,
}) {
  const speed = attempt?.inter_phase_speed || {};
  const knee = attempt?.knee_angle || {};
  const heel = attempt?.heel_contact || {};
  const ft = attempt?.follow_through || {};
  const ball = attempt?.ball_start || {};
  const outcome = attempt?.shot_outcome || 'not_visible';

  const phase = useMemo(
    () => getCurrentPhase(speed, clipStart, currentTime),
    [speed, clipStart, currentTime]
  );

  const timer = useMemo(
    () => getPhaseTimer(speed, clipStart, currentTime),
    [speed, clipStart, currentTime]
  );

  const phaseColor = PHASE_COLORS[phase] || '#6b7280';
  const outcomeColor = OUTCOME_COLORS[outcome] || '#6b7280';

  if (!visible || !attempt) return null;

  return (
    <div style={{
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      overflow: 'hidden',
      userSelect: 'none',
      zIndex: 10,
    }}>
      {/* Phase Badge — top-left */}
      <div style={{ position: 'absolute', top: 12, left: 12 }}>
        <div style={hudBadge(phaseColor)}>
          <div style={hudDot(phaseColor)} />
          {PHASE_LABELS[phase] || phase}
        </div>
      </div>

      {/* Outcome Badge — top-right (shows after release) */}
      {(phase === 'follow_through' || phase === 'recovery') && outcome !== 'not_visible' && (
        <div style={{ position: 'absolute', top: 12, right: 12 }}>
          <div style={hudBadge(outcomeColor)}>
            <div style={hudDot(outcomeColor)} />
            {outcome.replace(/_/g, ' ')}
          </div>
        </div>
      )}

      {/* Speed Timer — top-center */}
      {timer && (
        <div style={{ position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)' }}>
          <div style={{
            ...hudBadge('#6366f1'),
            fontSize: 14,
            fontWeight: 800,
            padding: '4px 16px',
          }}>
            <span style={{ color: '#a5b4fc', fontSize: 9, marginRight: 4 }}>
              {timer.from}→{timer.to}
            </span>
            {timer.label}
          </div>
        </div>
      )}

      {/* Knee Angle Indicator — bottom-left */}
      {(phase === 'dip' || phase === 'raise') && knee.min_knee_angle_degrees != null && (
        <div style={{ position: 'absolute', bottom: 60, left: 12 }}>
          <div style={hudBadge('#f59e0b')}>
            <div style={hudDot('#f59e0b')} />
            <span>Knee: {Math.round(knee.min_knee_angle_degrees)}°</span>
            {knee.dip_timing && knee.dip_timing !== 'not_detected' && (
              <span style={{ color: '#fbbf24', marginLeft: 4, fontSize: 9 }}>
                ({knee.dip_timing.replace(/_/g, ' ')})
              </span>
            )}
          </div>
          {/* Mini arc visualization */}
          <svg width="60" height="40" viewBox="0 0 60 40" style={{ marginTop: 4, opacity: 0.8 }}>
            <path
              d={`M 10 35 Q 30 ${35 - (180 - (knee.min_knee_angle_degrees || 160)) * 0.3} 50 35`}
              stroke="#f59e0b"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
            />
            <circle cx="10" cy="35" r="3" fill="#f59e0b" opacity="0.6" />
            <circle cx="30" cy={35 - (180 - (knee.min_knee_angle_degrees || 160)) * 0.3} r="3" fill="#f59e0b" />
            <circle cx="50" cy="35" r="3" fill="#f59e0b" opacity="0.6" />
          </svg>
        </div>
      )}

      {/* Heel Elevation Indicator — bottom-right */}
      {(phase === 'release' || phase === 'follow_through') && (
        <div style={{ position: 'absolute', bottom: 60, right: 12 }}>
          <div style={hudBadge('#22c55e')}>
            <div style={hudDot(
              heel.toe_duration_tenths > 0 ? '#22c55e' : '#6b7280'
            )} />
            <span>
              {heel.toe_duration_tenths > 0
                ? `Toes: ${heel.toe_duration_tenths.toFixed(1)}t`
                : 'Heel tracking...'}
            </span>
          </div>
          {/* Heel elevation bar */}
          <div style={{
            marginTop: 4,
            width: 80,
            height: 6,
            background: 'rgba(255,255,255,0.1)',
            borderRadius: 3,
            overflow: 'hidden',
          }}>
            <div style={{
              width: `${Math.min((heel.max_heel_elevation_px || 0) / 50 * 100, 100)}%`,
              height: '100%',
              background: '#22c55e',
              borderRadius: 3,
              transition: 'width 0.3s ease',
            }} />
          </div>
        </div>
      )}

      {/* Ball Start Position — shows during raise phase */}
      {phase === 'raise' && ball.height_relative_to_body && (
        <div style={{ position: 'absolute', bottom: 120, left: 12 }}>
          <div style={hudBadge('#8b5cf6')}>
            <div style={hudDot('#8b5cf6')} />
            <span>Ball: {ball.height_relative_to_body} / {ball.lateral_position || 'centered'}</span>
          </div>
        </div>
      )}

      {/* Follow-Through Hold — shows during follow-through */}
      {phase === 'follow_through' && (
        <div style={{ position: 'absolute', bottom: 120, right: 12 }}>
          <div style={hudBadge('#06b6d4')}>
            <div style={hudDot('#06b6d4')} />
            <span>
              Hold: {ft.hold_duration_tenths > 0 ? `${ft.hold_duration_tenths.toFixed(1)}t` : '...'}
              {ft.hold_stability && ft.hold_stability !== 'slight_drift' && (
                <span style={{ marginLeft: 4, fontSize: 9, color: '#67e8f9' }}>
                  ({ft.hold_stability.replace(/_/g, ' ')})
                </span>
              )}
            </span>
          </div>
          {ft.finger_direction && ft.finger_direction !== 'not_visible' && (
            <div style={{ ...hudBadge('#06b6d4'), marginTop: 4, fontSize: 9 }}>
              Fingers: {ft.finger_direction.replace(/_/g, ' ')}
            </div>
          )}
        </div>
      )}

      {/* Bottom HUD — Shot Speed (always visible after release) */}
      <div style={{
        position: 'absolute',
        bottom: 12,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: 8,
      }}>
        {speed.total_shot_speed_tenths > 0 && (phase === 'release' || phase === 'follow_through' || phase === 'recovery') && (
          <div style={{
            ...hudBadge('#6366f1'),
            fontSize: 13,
            padding: '5px 16px',
          }}>
            Shot Speed: {speed.total_shot_speed_tenths.toFixed(1)}t
          </div>
        )}
        {/* Shooter label */}
        {attempt.shooter && (
          <div style={hudBadge('#fff')}>
            {attempt.shooter} #{attempt.attempt_number || ''}
          </div>
        )}
      </div>

      {/* Phase Timeline — bottom bar */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: 4,
        display: 'flex',
      }}>
        {['stance', 'dip', 'raise', 'set_point', 'release', 'follow_through', 'recovery'].map(p => (
          <div
            key={p}
            style={{
              flex: 1,
              background: phase === p
                ? PHASE_COLORS[p]
                : `${PHASE_COLORS[p]}33`,
              transition: 'background 0.3s ease',
            }}
          />
        ))}
      </div>
    </div>
  );
}
